import {AfterViewInit, Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {ReplaySubject} from 'rxjs';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {AuthService} from '../../../../shared/services/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {FilterService} from '../../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../../shared/ngrx/reducers';
import {delay, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../../shared/interface/response/counted-and-paginated';
import {getCurrentRouteState} from '../../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../../shared/ngrx/route-serializer';
import {Uuid} from '../../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {Invoice} from '../../../../shared/interface/invoice';
import {InvoiceService} from '../../../../shared/services/invoice.service';
import {
  COMMISSION_SETTLEMENT_STATUS_TO_GENERATE,
  INVOICE_STATUS_PAID,
  INVOICE_STATUS_SENT_FOR_PAYMENT
} from "../../../../shared/services/dict.service";
import {ChangeStatusDialogComponent} from "../change-status-dialog/change-status-dialog.component";
import {Sort} from "@angular/material/sort";

@Component({
  selector: 'app-all-invoices-list',
  templateUrl: './all-invoices-list.component.html',
  styleUrls: ['./all-invoices-list.component.scss']
})
export class AllInvoicesListComponent implements OnDestroy, AfterViewInit {
  @Input() embeddedView = false;

  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  readonly INVOICE_STATUS_SENT_FOR_PAYMENT = INVOICE_STATUS_SENT_FOR_PAYMENT;
  readonly INVOICE_STATUS_PAID = INVOICE_STATUS_PAID;

  initialFilters: any;
  items: Invoice[] = [];
  totalData = 0;
  displayedColumns: string[] = [
    'billingMonth', 'company', 'number', 'party', 'status',
    'net', 'payoutNet', 'paymentDate', 'action'
  ];
  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'paymentDate';
  sortOrder = 'desc';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private invoiceService: InvoiceService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.invoiceService.invoiceListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<Invoice>) => {
          this.items = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      billingMonth: 'date',
      contains$number: 'string',
      ofwca: 'string',
      partner: 'string',
      in$status: 'int[]',
      gte$net: 'string',
      lte$net: 'string',
      assetsRegional: 'string',
      company: 'int',
      contractType: 'int',
      owner: 'string',
      selfBilling: 'int',
      automaticSettlement: 'int',
      sellerAccountNumber: 'int',
      generatedManually: 'int',
      serviceDate: 'date',
      invoiceDocumentTypeCharge: 'int',
      invoiceDocumentTypeCredit: 'int',
      mandateBill: 'int',
      gte$createdAt: 'date',
      lte$createdAt: 'date',
    }, filters);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.sortBy = state.params?.sortBy ? state.params.sortBy : 'paymentDate';
        this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'desc';
        this.loadListData();
      })
    ;
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }
  buildFilters(): any {
    const filters = {...this.initialFilters};

    return filters;
  }

  loadListData(): void {
    this.invoiceService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters(),
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      });
  }

  downloadReport(): void {
    this.invoiceService.downloadReport(this.buildFilters(),
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      })
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  changeStatus(
    actionCode: string,
    enableNote: boolean,
    noteLabel: string,
    newStatusName: string,
    invoice: Invoice,
  ): void {
    const dialogRef = this.dialog.open(ChangeStatusDialogComponent, {
      width: '500px',
      data: {
        invoice,
        enableNote,
        noteLabel,
        actionCode,
        newStatusName,
        sortBy: invoice,
      },
    });

    dialogRef.afterClosed().subscribe((t) => {
      this.loadListData();
    });

  }

  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;

    if (!this.embeddedView) {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        sortBy: $event.active,
        sortOrder: $event.direction,
        page: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value
      })]);
    } else {
      this.loadListData();
    }
  }

  delete(invoice: Invoice): void {
    this.invoiceService.delete(invoice.id)
      .subscribe({
        next: () => {
          this.snack.open('Pomyślnie usunięto fakturę', '', {duration: 2000});
          this.loadListData();
        },
        error: (error: HttpErrorResponse) => {
          this.snack.open('Nie można usunąć faktury', error.status.toString(), {duration: 2000});
        }
      });
  }
}
